import React, { useEffect, useState } from "react";
import { Modal, LegacyStack, Text } from "@shopify/polaris";
import horseLogo from "images/horse-logo-110.png";
import purchaseOrderIcon from "images/purchase-order-icon.png";
import { getReadBanner, createReadBanner } from "../../../api_utils/requests";
import { useNavigate } from "react-router-dom";
import { ONBOARDING_BANNER_KEY, ONBOARDING_RECOMMENDATIONS_BANNER_KEY } from "../../../constants";

const steps = {
  1: {
    header: "Congratulations! Your store is now imported",
    body: "Horse has successfully imported and analyzed your store data. You're now ready to see personalized recommendations.",
    logo: horseLogo,
  },
  2: {
    header: "Get started with your first purchase order",
    body: "Start by making a trial purchase order or simply check out your sales trends.",
    logo: purchaseOrderIcon,
  },
};

interface OnboardingRecommendationsProps {
  readonly readyForRecommendations: boolean;
}

function OnboardingRecommendations({ readyForRecommendations }: OnboardingRecommendationsProps): React.ReactElement {
  const [firstOnboardingCompleted, setFirstOnboardingCompleted] = useState(false);
  const [alreadySeen, setAlreadySeen] = useState(true);
  const [onboardingStep, setOnboardingStep] = useState(1);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if first onboarding has been completed
    void getReadBanner(ONBOARDING_BANNER_KEY).then(() => {
      setFirstOnboardingCompleted(true);
    }).catch(() => {
      // First onboarding not completed, don't show this one
      setFirstOnboardingCompleted(false);
    });

    // Check if this recommendations onboarding has been seen
    void getReadBanner(ONBOARDING_RECOMMENDATIONS_BANNER_KEY).catch((e: unknown) => {
      if (e instanceof Error && e.message.includes("404")) {
        setAlreadySeen(false);
      }
    });
  }, []);

  const dismissBanner = async (): Promise<void> => {
    setAlreadySeen(true);
    await createReadBanner(ONBOARDING_RECOMMENDATIONS_BANNER_KEY);
  };

  let actions;
  if (onboardingStep === Object.keys(steps).length) {
    actions = {
      primaryAction: {
        content: "Make a purchase order",
        onAction: () => {
          void dismissBanner();
          setTimeout(() => {
            navigate("/purchase_orders/new");
          });
        },
      },
      secondaryActions: {
        content: "Sales trends",
        onAction: () => {
          void dismissBanner();
          setTimeout(() => {
            navigate("/sales_trends");
          });
        },
      },
    };
  } else {
    actions = {
      secondaryActions: {
        content: "Next",
        onAction: () => {
          setOnboardingStep(onboardingStep + 1);
        },
      },
    };
  }

  // Only show if first onboarding is completed, shop is ready for recommendations, and this onboarding hasn't been seen
  const shouldShow = firstOnboardingCompleted && readyForRecommendations && !alreadySeen;

  return (
    <Modal onClose={dismissBanner} open={shouldShow} small titleHidden {...{ ...actions }}>
      <Modal.Section>
        <LegacyStack distribution="center">
          <img height={110} src={steps[onboardingStep].logo} style={{ margin: "1em" }} />
        </LegacyStack>
        <LegacyStack distribution="center">
          <Text as="h2" variant="headingMd">
            {steps[onboardingStep].header}
          </Text>
        </LegacyStack>
        <LegacyStack distribution="center">
          <p className="text-center">{steps[onboardingStep].body}</p>
        </LegacyStack>
      </Modal.Section>
    </Modal>
  );
}

export default OnboardingRecommendations;
